import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Link, router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button, Input, DynamicIsland } from '../../../components/ui';
import { Section } from '../../../components/layout/AppShell';
import { cn } from '../../../lib/cn';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';

const ACCEPTED_EXTENSIONS = ['.epub', '.pdf', '.pptx'];
const ACCEPTED_MIME_TYPES = '.epub,.pdf,.pptx,application/epub+zip,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation';

const PROCESSING_STEPS = [
  'Parsing document structure...',
  'Extracting chapters...',
  'Creating text chunks...',
  'Generating embeddings...',
  'Building search index...',
];

const CATEGORY_LABELS = {
  novel: 'Novel',
  article: 'Article',
  non_fiction: 'Non-Fiction',
  textbook: 'Textbook',
  reference: 'Reference',
  presentation: 'Presentation',
  poetry: 'Poetry',
  drama: 'Drama',
  other: 'Other',
};

const ITEM_TYPE_CONFIG = {
  epub: { label: 'EPUB', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  pdf: { label: 'PDF', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  pptx: { label: 'PPTX', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  link: { label: 'Link', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' },
};

export default function LibraryIndex({ books, categories = [] }) {
  const { t } = useTranslation();
  const { clear: clearBreadcrumbs } = useBreadcrumbs();
  useEffect(() => {
    // Section index is a dead-end relative to the vertical-nav axis; clear any
    // stale trail (e.g. book/chapter from a prior ChapterReader visit).
    clearBreadcrumbs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [savingLink, setSavingLink] = useState(false);
  const [uploadMode, setUploadMode] = useState('file'); // 'file' | 'link'
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [seriesFilter, setSeriesFilter] = useState(null);
  const fileInputRef = useRef(null);

  const hasFilters = searchQuery || categoryFilter || typeFilter || seriesFilter;

  const filteredBooks = useMemo(() => {
    return books.filter(book => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!book.title?.toLowerCase().includes(q) && !book.author?.toLowerCase().includes(q)) return false;
      }
      if (categoryFilter && book.category !== categoryFilter) return false;
      if (typeFilter && book.item_type !== typeFilter) return false;
      if (seriesFilter && book.book_series_id !== seriesFilter) return false;
      return true;
    });
  }, [books, searchQuery, categoryFilter, typeFilter, seriesFilter]);

  const clearFilters = () => { setSearchQuery(''); setCategoryFilter(null); setTypeFilter(null); setSeriesFilter(null); };

  const processingBooks = filteredBooks.filter(b => b.status === 'processing');
  const readyBooks = filteredBooks.filter(b => b.status === 'ready');
  const failedBooks = filteredBooks.filter(b => b.status === 'failed');

  // Poll for updates when there are processing items (use unfiltered list)
  const hasProcessing = books.some(b => b.status === 'processing');
  useEffect(() => {
    if (!hasProcessing) return;

    const pollInterval = setInterval(() => {
      router.reload({ only: ['books'], preserveScroll: true });
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [hasProcessing]);

  const handleUpload = useCallback((file) => {
    if (!file || uploading) return;

    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      alert('Please select an EPUB, PDF, or PPTX file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name.replace(/\.(epub|pdf|pptx)$/i, ''));

    router.post('/teacher/library', formData, {
      forceFormData: true,
      onFinish: () => setUploading(false),
    });
  }, [uploading]);

  const handleSaveLink = useCallback((e) => {
    e.preventDefault();
    if (!linkUrl.trim() || savingLink) return;

    setSavingLink(true);
    router.post('/teacher/library', { url: linkUrl.trim() }, {
      onFinish: () => {
        setSavingLink(false);
        setLinkUrl('');
      },
    });
  }, [linkUrl, savingLink]);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    e.target.value = '';
  }, [handleUpload]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  }, [handleUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const isProcessing = hasProcessing || uploading;

  // Collect which categories actually exist in the library for filter chips
  const usedCategories = useMemo(() => {
    const cats = new Set(books.map(b => b.category).filter(Boolean));
    return categories.filter(c => cats.has(c));
  }, [books, categories]);

  const usedTypes = useMemo(() => {
    return [...new Set(books.map(b => b.item_type))];
  }, [books]);

  // Distinct series present in the library, for the filter chips. Series
  // are per-teacher data (not a fixed enum like categories), so this is
  // derived from the books prop rather than passed separately.
  const usedSeries = useMemo(() => {
    const seen = new Map();
    books.forEach(b => {
      if (b.book_series_id && b.series_name && !seen.has(b.book_series_id)) {
        seen.set(b.book_series_id, b.series_name);
      }
    });
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [books]);

  return (
    <TeacherShell scrollTitle={t('teacherLibrary.title')}>
      <DynamicIsland
        isActive={isProcessing}
        steps={uploading ? ['Uploading file...'] : PROCESSING_STEPS}
        stepDuration={2000}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_MIME_TYPES}
        onChange={handleFileChange}
        className="hidden"
        disabled={uploading}
      />

      <TeacherPageHeader
        title={t('teacherLibrary.title')}
        subtitle={t('teacherLibrary.subtitle')}
        actions={
          <div className="flex items-center gap-3">
            <Link
              href="/teacher/book_series"
              className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Series
            </Link>
            <Button
              variant="primary"
              onClick={openFilePicker}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <Spinner className="w-4 h-4 mr-1.5" />
                  {t('common.uploading')}
                </>
              ) : (
                <>
                  <UploadIcon className="w-4 h-4 mr-1.5" />
                  {t('teacherLibrary.uploadFile')}
                </>
              )}
            </Button>
          </div>
        }
      />

      {books.length === 0 ? (
        <div className="space-y-8">
          <DropZone
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={openFilePicker}
            isDragOver={dragOver}
            uploading={uploading}
          />
          <LinkInput
            linkUrl={linkUrl}
            setLinkUrl={setLinkUrl}
            onSubmit={handleSaveLink}
            saving={savingLink}
          />
        </div>
      ) : (
        <div className="space-y-10">
          {/* Upload controls */}
          <div className="space-y-3">
            {/* Mode toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setUploadMode('file')}
                className={cn(
                  "text-sm px-3 py-1 rounded-full transition-colors",
                  uploadMode === 'file'
                    ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                    : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                )}
              >
                {t('teacherLibrary.uploadFile')}
              </button>
              <button
                onClick={() => setUploadMode('link')}
                className={cn(
                  "text-sm px-3 py-1 rounded-full transition-colors",
                  uploadMode === 'link'
                    ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                    : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                )}
              >
                {t('teacherLibrary.saveLink')}
              </button>
            </div>

            {uploadMode === 'file' ? (
              <DropZone
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={openFilePicker}
                isDragOver={dragOver}
                uploading={uploading}
                compact
              />
            ) : (
              <LinkInput
                linkUrl={linkUrl}
                setLinkUrl={setLinkUrl}
                onSubmit={handleSaveLink}
                saving={savingLink}
              />
            )}
          </div>

          {/* Search & filter bar */}
          <div className="space-y-3">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('teacherLibrary.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/20"
              />
            </div>

            {(usedCategories.length > 0 || usedTypes.length > 1 || usedSeries.length > 0) && (
              <div className="flex flex-wrap items-center gap-2">
                {usedTypes.length > 1 && usedTypes.map(type => {
                  const config = ITEM_TYPE_CONFIG[type];
                  return (
                    <button
                      key={type}
                      onClick={() => setTypeFilter(typeFilter === type ? null : type)}
                      className={cn(
                        "text-xs px-2.5 py-1 rounded-full transition-colors",
                        typeFilter === type
                          ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                          : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                      )}
                    >
                      {config?.label || type}
                    </button>
                  );
                })}

                {usedTypes.length > 1 && usedCategories.length > 0 && (
                  <span className="w-px h-4 bg-gray-200 dark:bg-gray-700" />
                )}

                {usedCategories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setCategoryFilter(categoryFilter === cat ? null : cat)}
                    className={cn(
                      "text-xs px-2.5 py-1 rounded-full transition-colors",
                      categoryFilter === cat
                        ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                        : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                    )}
                  >
                    {CATEGORY_LABELS[cat] || cat}
                  </button>
                ))}

                {(usedTypes.length > 1 || usedCategories.length > 0) && usedSeries.length > 0 && (
                  <span className="w-px h-4 bg-gray-200 dark:bg-gray-700" />
                )}

                {usedSeries.map(series => (
                  <button
                    key={series.id}
                    onClick={() => setSeriesFilter(seriesFilter === series.id ? null : series.id)}
                    className={cn(
                      "text-xs px-2.5 py-1 rounded-full transition-colors",
                      seriesFilter === series.id
                        ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                        : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                    )}
                  >
                    {series.name}
                  </button>
                ))}

                {hasFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ml-1"
                  >
                    {t('common.clear')}
                  </button>
                )}
              </div>
            )}

            {hasFilters && (
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Showing {filteredBooks.length} of {books.length} items
              </p>
            )}
          </div>

          {hasFilters && filteredBooks.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400 mb-3">{t('common.noResults')}</p>
              <Button variant="secondary" size="sm" onClick={clearFilters}>{t('common.clear')}</Button>
            </div>
          ) : (<>

          {processingBooks.length > 0 && (
            <Section title={t('common.processing')} description={t('teacherLibrary.processingItems')}>
              <BookGrid books={processingBooks} />
            </Section>
          )}

          {readyBooks.length > 0 && (
            <Section title={t('common.ready')} description={t('teacherLibrary.readyItems')}>
              <BookGrid books={readyBooks} />
            </Section>
          )}

          {failedBooks.length > 0 && (
            <Section title={t('common.failed')} description={t('teacherLibrary.failedItems')}>
              <BookGrid books={failedBooks} />
            </Section>
          )}

          </>)}
        </div>
      )}
    </TeacherShell>
  );
}

function LinkInput({ linkUrl, setLinkUrl, onSubmit, saving }) {
  const { t } = useTranslation();
  return (
    <form onSubmit={onSubmit} className="flex gap-2">
      <Input
        type="url"
        placeholder="https://example.com/article"
        value={linkUrl}
        onChange={(e) => setLinkUrl(e.target.value)}
        className="flex-1"
        disabled={saving}
      />
      <Button
        type="submit"
        variant="secondary"
        disabled={saving || !linkUrl.trim()}
      >
        {saving ? <Spinner className="w-4 h-4" /> : t('teacherLibrary.saveLink')}
      </Button>
    </form>
  );
}

function DropZone({ onDrop, onDragOver, onDragLeave, onClick, isDragOver, uploading, compact = false }) {
  const { t } = useTranslation();
  if (compact) {
    return (
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={onClick}
        className={cn(
          "border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors",
          isDragOver
            ? "border-gray-400 bg-gray-50 dark:border-gray-500 dark:bg-gray-800"
            : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
        )}
      >
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {uploading ? (
            <span className="flex items-center justify-center gap-2">
              <Spinner className="w-4 h-4" />
              {t('common.uploading')}
            </span>
          ) : (
            <>{t('teacherLibrary.dropHere')}</>
          )}
        </p>
      </div>
    );
  }

  return (
    <div
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onClick={onClick}
      className={cn(
        "border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all",
        isDragOver
          ? "border-gray-400 bg-gray-50 dark:border-gray-500 dark:bg-gray-800 scale-[1.02]"
          : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
      )}
    >
      <div className="max-w-sm mx-auto">
        <BookStackIcon className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {uploading ? t('common.uploading') : t('teacherLibrary.addFirstItem')}
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          {t('teacherLibrary.addFirstItemHelp')}
        </p>
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400">
            <Spinner className="w-5 h-5" />
            {t('teacherLibrary.processingUpload')}
          </div>
        ) : (
          <Button variant="secondary" onClick={(e) => { e.stopPropagation(); onClick(); }}>
            <UploadIcon className="w-4 h-4 mr-1.5" />
            {t('teacherLibrary.chooseFile')}
          </Button>
        )}
      </div>
    </div>
  );
}

function BookGrid({ books }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {books.map((book) => (
        <BookCard key={book.id} book={book} />
      ))}
    </div>
  );
}

function BookCard({ book }) {
  const { t } = useTranslation();
  const statusStyles = {
    processing: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    ready: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };

  const typeConfig = ITEM_TYPE_CONFIG[book.item_type] || ITEM_TYPE_CONFIG.epub;

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (confirm('Remove this item from your library?')) {
      router.delete(`/teacher/library/${book.id}`);
    }
  };

  const isClickable = book.status === 'ready';
  const Wrapper = isClickable ? Link : 'div';
  const wrapperProps = isClickable ? { href: `/teacher/library/${book.id}` } : {};

  const ItemTypeIcon = book.item_type === 'link' ? LinkIcon
    : book.item_type === 'pptx' ? PresentationIcon
    : book.item_type === 'pdf' ? FileTextIcon
    : BookStackIcon;

  return (
    <Wrapper {...wrapperProps}>
      <Card className={cn(
        "h-full flex flex-row relative group overflow-hidden",
        isClickable && "hover:shadow-md transition-shadow cursor-pointer"
      )}>
        {/* Delete button - only for non-ready items (processing/failed) */}
        {book.status !== 'ready' && (
          <button
            onClick={handleDelete}
            className={cn(
              "absolute top-2 right-2 z-10 w-6 h-6 rounded-full",
              "flex items-center justify-center",
              "bg-white/80 dark:bg-gray-700/80 text-gray-400 dark:text-gray-500",
              "hover:bg-red-100 hover:text-red-500 dark:hover:bg-red-900/30 dark:hover:text-red-400",
              "opacity-0 group-hover:opacity-100 transition-opacity",
              "focus:opacity-100 focus:outline-none"
            )}
            title="Remove item"
          >
            <CloseIcon className="w-3.5 h-3.5" />
          </button>
        )}

        {/* Cover image or type icon - left side */}
        {book.cover_image_url ? (
          <div className="w-28 shrink-0 bg-gray-100 dark:bg-gray-800">
            <img
              src={book.cover_image_url}
              alt={book.title}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="w-28 shrink-0 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center">
            <ItemTypeIcon className="w-10 h-10 text-gray-300 dark:text-gray-600" />
          </div>
        )}

        {/* Content - right side */}
        <div className="p-4 flex flex-col flex-1 min-w-0">
          <div className="flex items-start gap-2 mb-2">
            <span className={cn(
              'text-xs font-medium px-2 py-0.5 rounded',
              statusStyles[book.status]
            )}>
              {book.status}
            </span>
            <span className={cn(
              'text-xs font-medium px-2 py-0.5 rounded',
              typeConfig.color
            )}>
              {typeConfig.label}
            </span>
            {book.category && (
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {CATEGORY_LABELS[book.category] || book.category}
              </span>
            )}
          </div>

          <h3 className="font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2">
            {book.title}
          </h3>

          {book.series_name && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">
              {t('teacherLibrary.partOf', { series: book.series_name })}
              {book.position_in_series != null && ` · #${book.position_in_series}`}
            </p>
          )}

          {book.author && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              by {book.author}
            </p>
          )}

          {book.item_type === 'link' && book.url && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-2 truncate">
              {book.url}
            </p>
          )}

          {book.status === 'ready' && (
            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mt-auto pt-2 border-t border-gray-100 dark:border-gray-700">
              <span>{book.chapter_count} {book.chapter_count === 1 ? t('common.chapter') : t('common.chapters')}</span>
              <span>{book.chunk_count} {t('common.pages')}</span>
            </div>
          )}

          {book.status === 'processing' && (
            <div className="mt-auto pt-2">
              <div className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-400">
                <Spinner className="w-4 h-4" />
                {t('common.processing')}
              </div>
            </div>
          )}

          {book.status === 'failed' && book.error_message && (
            <p className="text-sm text-red-500 mt-2 line-clamp-2">
              {book.error_message}
            </p>
          )}
        </div>
      </Card>
    </Wrapper>
  );
}

function Spinner({ className }) {
  return (
    <svg className={cn("animate-spin", className)} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

function UploadIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  );
}

function BookStackIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  );
}

function FileTextIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function PresentationIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h18v12H3V3zm4 15l5-3 5 3" />
    </svg>
  );
}

function LinkIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  );
}

function SearchIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function CloseIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
